import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useNavigate } from 'react-router-dom';
import { NavBar } from '../../components/NavBar';
import { api } from '../../lib/api';
import type { Home } from '@home-guard/shared';

const DAYS = ['monday','tuesday','wednesday','thursday','friday','saturday','sunday'] as const;
type Day = typeof DAYS[number];

const schema = z.object({
  trash_day:           z.enum(DAYS).nullable().optional(),
  recycling_day:       z.enum([...DAYS, 'biweekly']).nullable().optional(),
  recycling_frequency: z.enum(['weekly','biweekly','monthly']).nullable().optional(),
  bulk_pickup_day:     z.string().max(50).trim().nullable().optional(),
  trash_provider:      z.string().max(100).trim().nullable().optional(),
});
type FormData = z.infer<typeof schema>;

function dayLabel(d: string): string {
  return d.charAt(0).toUpperCase() + d.slice(1);
}

function nextPickupDate(day: string | null | undefined): string | null {
  if (!day || day === 'biweekly') return null;
  const dayIndex: Record<string, number> = {
    sunday: 0, monday: 1, tuesday: 2, wednesday: 3,
    thursday: 4, friday: 5, saturday: 6,
  };
  const target = dayIndex[day];
  if (target === undefined) return null;
  const today = new Date();
  let daysUntil = (target - today.getDay() + 7) % 7;
  if (daysUntil === 0) daysUntil = 7;
  const next = new Date(today);
  next.setDate(today.getDate() + daysUntil);
  return next.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' });
}

export function TrashReminder() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { data: homesData } = useQuery({
    queryKey: ['homes'],
    queryFn: () => api.get<{ data: Home[] }>('/homes'),
  });
  const homes = homesData?.data ?? [];
  const home = homes.find(h => h.is_primary) ?? homes[0];

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: {
      trash_day:           (home?.trash_day as Day | null) ?? null,
      recycling_day:       (home?.recycling_day as Day | 'biweekly' | null) ?? null,
      recycling_frequency: (home?.recycling_frequency as 'weekly' | 'biweekly' | 'monthly' | null) ?? null,
      bulk_pickup_day:     home?.bulk_pickup_day ?? null,
      trash_provider:      home?.trash_provider ?? null,
    },
  });

  const trashDay     = watch('trash_day');
  const recyclingDay = watch('recycling_day');
  const nextTrash    = nextPickupDate(trashDay);
  const nextRecycle  = nextPickupDate(recyclingDay);

  const mutation = useMutation({
    mutationFn: (data: FormData) => api.put<Home>(`/homes/${home!.id}`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['homes'] });
      navigate('/app/dashboard');
    },
  });

  if (!home) return null;

  const onSubmit = (data: FormData) => mutation.mutate(data);

  function DaySelector({ name, value }: { name: 'trash_day' | 'recycling_day'; value: string | null | undefined }) {
    const options: string[] = name === 'recycling_day' ? [...DAYS, 'biweekly'] : [...DAYS];
    return (
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 8 }}>
        {options.map(d => (
          <button
            key={d}
            type="button"
            onClick={() => setValue(name, d === value ? null : (d as Day))}
            style={{
              padding: '8px 12px', borderRadius: 8, fontSize: 13, fontWeight: 500,
              border: value === d ? '2px solid #1D4ED8' : '1.5px solid #d1d5db',
              background: value === d ? '#eff6ff' : '#fff',
              color: value === d ? '#1D4ED8' : '#374151',
              cursor: 'pointer',
            }}
          >
            {dayLabel(d)}
          </button>
        ))}
        {value && (
          <button
            type="button"
            onClick={() => setValue(name, null)}
            style={{
              padding: '8px 12px', borderRadius: 8, fontSize: 13, fontWeight: 500,
              border: '1.5px solid #fecaca', background: '#fef2f2', color: '#ef4444', cursor: 'pointer',
            }}
          >
            Clear
          </button>
        )}
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100dvh', background: '#f9fafb', paddingBottom: 100 }}>
      <div style={{ background: '#1D4ED8', padding: '48px 20px 24px' }}>
        <button
          onClick={() => navigate(-1)}
          style={{ background: 'rgba(255,255,255,0.15)', border: 'none', color: '#fff',
            borderRadius: 8, padding: '8px 12px', fontSize: 13, cursor: 'pointer', marginBottom: 16 }}
        >
          ← Back
        </button>
        <h1 style={{ margin: 0, fontSize: 24, fontWeight: 700, color: '#fff' }}>Trash & Recycling</h1>
        <p style={{ margin: '4px 0 0', fontSize: 14, color: 'rgba(255,255,255,0.75)' }}>
          Set your pickup schedule for weekly reminders
        </p>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} style={{ padding: '20px 16px', display: 'flex', flexDirection: 'column', gap: 16 }}>
        {/* Trash day */}
        <div style={{ background: '#fff', borderRadius: 12, padding: '16px', border: '1.5px solid #e5e7eb' }}>
          <p style={{ margin: 0, fontWeight: 600, fontSize: 15, color: '#111827' }}>Trash pickup day</p>
          <p style={{ margin: '2px 0 8px', fontSize: 13, color: '#6b7280' }}>
            You will get a reminder the evening before pickup
          </p>
          <DaySelector name="trash_day" value={trashDay} />
          {nextTrash && (
            <div style={{
              marginTop: 12, padding: '10px 12px', background: '#f0fdf4',
              borderRadius: 8, fontSize: 13, color: '#15803d', fontWeight: 500,
            }}>
              Next pickup: {nextTrash}
            </div>
          )}
          {errors.trash_day && <p style={{ color: '#ef4444', fontSize: 12, marginTop: 6 }}>{errors.trash_day.message}</p>}
        </div>

        {/* Trash provider */}
        <div style={{ background: '#fff', borderRadius: 12, padding: '16px', border: '1.5px solid #e5e7eb' }}>
          <label style={{ fontWeight: 600, fontSize: 15, color: '#111827', display: 'block', marginBottom: 8 }}>
            Trash provider name (optional)
          </label>
          <input
            {...register('trash_provider')}
            placeholder="e.g. Waste Management, Republic Services"
            style={{
              width: '100%', padding: '10px 12px', borderRadius: 8, border: '1.5px solid #d1d5db',
              fontSize: 15, boxSizing: 'border-box',
            }}
          />
        </div>

        {/* Recycling day */}
        <div style={{ background: '#fff', borderRadius: 12, padding: '16px', border: '1.5px solid #e5e7eb' }}>
          <p style={{ margin: 0, fontWeight: 600, fontSize: 15, color: '#111827' }}>Recycling pickup day</p>
          <p style={{ margin: '2px 0 8px', fontSize: 13, color: '#6b7280' }}>
            Select "Biweekly" if your recycling alternates weeks
          </p>
          <DaySelector name="recycling_day" value={recyclingDay} />
          {nextRecycle && (
            <div style={{
              marginTop: 12, padding: '10px 12px', background: '#eff6ff',
              borderRadius: 8, fontSize: 13, color: '#1D4ED8', fontWeight: 500,
            }}>
              Next recycling pickup: {nextRecycle}
            </div>
          )}
        </div>

        {/* Recycling frequency */}
        <div style={{ background: '#fff', borderRadius: 12, padding: '16px', border: '1.5px solid #e5e7eb' }}>
          <p style={{ margin: '0 0 10px', fontWeight: 600, fontSize: 15, color: '#111827' }}>
            Recycling frequency
          </p>
          {(['weekly','biweekly','monthly'] as const).map(freq => (
            <label
              key={freq}
              style={{
                display: 'flex', alignItems: 'center', gap: 10,
                padding: '10px 0',
                borderBottom: freq !== 'monthly' ? '1px solid #f3f4f6' : 'none',
                cursor: 'pointer',
              }}
            >
              <input {...register('recycling_frequency')} type="radio" value={freq} style={{ accentColor: '#1D4ED8' }} />
              <span style={{ fontSize: 15, color: '#111827' }}>
                {freq === 'weekly' ? 'Every week' : freq === 'biweekly' ? 'Every other week' : 'Once a month'}
              </span>
            </label>
          ))}
        </div>

        {/* Bulk pickup day */}
        <div style={{ background: '#fff', borderRadius: 12, padding: '16px', border: '1.5px solid #e5e7eb' }}>
          <label style={{ fontWeight: 600, fontSize: 15, color: '#111827', display: 'block', marginBottom: 4 }}>
            Bulk / heavy-item pickup day (optional)
          </label>
          <p style={{ margin: '0 0 8px', fontSize: 13, color: '#6b7280' }}>
            Enter the day of month or description (e.g. "1st Monday", "Day 15")
          </p>
          <input
            {...register('bulk_pickup_day')}
            placeholder="e.g. 1st Monday, Day 15 of month"
            style={{
              width: '100%', padding: '10px 12px', borderRadius: 8, border: '1.5px solid #d1d5db',
              fontSize: 15, boxSizing: 'border-box',
            }}
          />
        </div>

        {mutation.error instanceof Error && (
          <div style={{
            padding: '10px 14px', background: '#fef2f2', border: '1px solid #fecaca',
            borderRadius: 8, color: '#ef4444', fontSize: 14,
          }}>
            Failed to save. Please try again.
          </div>
        )}

        <button
          type="submit"
          disabled={isSubmitting || mutation.isPending}
          style={{
            padding: '14px', background: '#1D4ED8', color: '#fff', border: 'none',
            borderRadius: 12, fontWeight: 600, fontSize: 16, cursor: 'pointer', opacity: isSubmitting ? 0.7 : 1,
          }}
        >
          {isSubmitting || mutation.isPending ? 'Saving…' : 'Save schedule'}
        </button>
      </form>

      <NavBar />
    </div>
  );
}
